import { useCallback, useEffect, useRef, useState } from 'react';
import WebView from '../../../utils';
import { BiometricScanCallback, BiometryStatus, LoginData } from './interface';

export const useBiometry = () => {
	const [isBiometryAvailable, setIsBiometryAvailable] = useState<boolean>(false);
	const [isBiometryEnabled, setIsBiometryEnabled] = useState<boolean>(false);
	const [biometricEmail, setBiometricEmail] = useState<string>();
	const [biometryStatus, setBiometryStatus] = useState<BiometryStatus>('default');
	const uidRef = useRef(new Date().getTime());
	const callbackRef = useRef<BiometricScanCallback>(() => {});

	useEffect(() => {
		const id = `useBiometry-${uidRef.current}`;
		WebView.subscribe(id, (eventName, message) => {
			switch (eventName) {
				case 'IS_BIOMETRIC_SETTING_AVAILABLE':
					setIsBiometryAvailable(message.isAvailable);
					break;
				case 'IS_BIOMETRIC_ENABLED':
					setIsBiometryEnabled(message.isEnabled);
					break;
				case 'GET_BIOMETRIC_EMAIL':
					setBiometricEmail(message.email);
					break;
				case 'BIOMETRIC_SCAN_SUCCEED':
					callbackRef.current(true, message);
					setBiometryStatus('success');
					break;
				case 'BIOMETRIC_SCAN_FAILED':
					callbackRef.current(false);
					setBiometryStatus('error');
					break;
			}
		});
		return () => WebView.unsubscribe(id);
	}, []);

	const triggerNewBiometricSession = useCallback(({ email, password }: LoginData) => {
		WebView.broadcast('NEW_BIOMETRIC_SESSION', { email, password });
	}, []);

	const triggerGetBiometricEmail = useCallback(() => {
		WebView.broadcast('GET_BIOMETRIC_EMAIL');
	}, []);

	const triggerBiometricScan = useCallback(() => {
		WebView.broadcast('TRIGGER_BIOMETRIC_SCAN');
		setBiometryStatus('loading');
	}, [setBiometryStatus]);

	const triggerGetIsBiometryEnabled = useCallback(() => {
		WebView.broadcast('IS_BIOMETRIC_ENABLED');
	}, []);

	const triggerGetIsBiometryAvailable = useCallback(() => {
		WebView.broadcast('IS_BIOMETRIC_SETTING_AVAILABLE');
	}, []);

	const triggerSetIsBiometryEnabled = useCallback(
		(isEnabled: boolean) => {
			WebView.broadcast('IS_BIOMETRIC_ENABLED_CHANGED', { isEnabled });
			setIsBiometryEnabled(isEnabled);
		},
		[setIsBiometryEnabled],
	);

	const triggerSetLoginFailed = useCallback(() => {
		WebView.broadcast('LOGIN_FAILED');
		setIsBiometryEnabled(false);
		localStorage.setItem('enableBiometry', 'false');
	}, []);

	return {
		isBiometryAvailable,
		isBiometryEnabled,
		biometricEmail,
		biometryStatus,

		subscribeToBiometricScan: (callback: BiometricScanCallback) => {
			callbackRef.current = callback;
		},

		triggerNewBiometricSession,
		triggerBiometricScan,
		triggerSetIsBiometryEnabled,
		triggerSetLoginFailed,

		refresh: useCallback(() => {
			triggerGetIsBiometryAvailable();
			triggerGetIsBiometryEnabled();
			triggerGetBiometricEmail();
		}, [triggerGetIsBiometryAvailable, triggerGetIsBiometryEnabled, triggerGetBiometricEmail]),
	};
};
